/**
 * Value Parsers
 *
 * Consolidates all value normalization logic that was previously duplicated
 * across SubmissionDetailPage, FormSubmissionsPage, RepeatedFormTableDisplay,
 * and ReadOnlyFormRenderer. Each parser handles every known storage format
 * (JSON string, array, CSV, object) and returns a consistent output type.
 */

/**
 * Parse checkbox group values into a string array.
 * Handles: Array, JSON string of array, comma-separated string.
 * @param {*} value - Raw field value
 * @returns {string[]}
 */
export function parseCheckboxGroup(value) {
  if (!value) return [];
  if (Array.isArray(value)) return value.map(String);
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed)) return parsed.map(String);
    } catch {
      // not JSON
    }
    if (value.trim()) {
      return value.split(',').map(v => v.trim()).filter(Boolean);
    }
  }
  return [];
}

/**
 * Parse tag list values into a string array.
 * Handles: Array, plain string (returned as single-element or split by comma).
 * @param {*} value - Raw field value
 * @returns {string[]}
 */
export function parseTagList(value) {
  if (!value) return [];
  if (Array.isArray(value)) return value.map(String);
  if (typeof value === 'string') return [value];
  return [];
}

/**
 * Parse coordinates from various storage formats.
 * Handles: { latitude, longitude, accuracy }, { lat, lng }, JSON string.
 * @param {*} value - Raw field value
 * @returns {{ latitude: number, longitude: number, accuracy?: number } | null}
 */
export function parseCoordinates(value) {
  if (!value) return null;
  let obj = value;
  if (typeof value === 'string') {
    try { obj = JSON.parse(value); } catch { return null; }
  }
  if (typeof obj !== 'object') return null;

  const lat = obj.latitude ?? obj.lat;
  const lng = obj.longitude ?? obj.lng ?? obj.lon;
  if (lat == null || lng == null) return null;

  return {
    latitude: Number(lat),
    longitude: Number(lng),
    accuracy: obj.accuracy != null ? Number(obj.accuracy) : undefined,
  };
}

/**
 * Parse image/file list values into an array of URL strings.
 * Handles: Array of strings, Array of objects with url/uri, JSON string, single string.
 * @param {*} value - Raw field value
 * @returns {string[]}
 */
export function parseImageList(value) {
  if (!value) return [];
  let arr = value;
  if (typeof value === 'string') {
    try { arr = JSON.parse(value); } catch { return [value]; }
  }
  if (!Array.isArray(arr)) return typeof arr === 'string' ? [arr] : [];
  return arr.map(item => {
    if (typeof item === 'string') return item;
    if (item && typeof item === 'object') return item.url || item.uri || item.path || '';
    return '';
  }).filter(Boolean);
}

/**
 * Normalize attachment objects to a consistent shape.
 * The backend DTO uses `attachmentType`/`fileUrl`; value components expect
 * `type`/`url`. This ensures both property names are available.
 * @param {Array} attachments - Raw attachment array from API
 * @returns {Array}
 */
export function normalizeAttachments(attachments) {
  if (!attachments) return [];
  return attachments.map(a => ({
    ...a,
    type: a.type || a.attachmentType,
    url: a.url || a.fileUrl,
  }));
}

/**
 * Resolve a single option value to its display label.
 * @param {string} value - The option value
 * @param {Array<{value: string, label: string}>} options - Available options
 * @returns {string}
 */
export function resolveOptionLabel(value, options) {
  if (!value) return '';
  if (!options || !Array.isArray(options)) return String(value);
  const option = options.find(opt => opt.value === value || opt.value === String(value));
  return option ? option.label : String(value);
}

/**
 * Resolve multiple option values to their display labels.
 * @param {*} value - Raw value (array, JSON string, CSV)
 * @param {Array<{value: string, label: string}>} options - Available options
 * @returns {string[]}
 */
export function resolveOptionLabels(value, options) {
  const values = parseCheckboxGroup(value);
  return values.map(v => resolveOptionLabel(v, options));
}
